import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Space, Tag, Descriptions, Table, message, Spin, Typography, Divider, Modal, Form, Input } from 'antd';
import { ArrowLeftOutlined, CheckCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiClient from '../services/api';
import { DisposalRequest, DisposalRequestItem } from '../types/disposal';
import { useAuth } from '../hooks/useAuth';

const { Title, Text } = Typography;

const DisposalDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [disposalRequest, setDisposalRequest] = useState<DisposalRequest | null>(null);
  const [disposalItems, setDisposalItems] = useState<DisposalRequestItem[]>([]);
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [approveForm] = Form.useForm();

  useEffect(() => {
    if (id) {
      loadDisposalRequestDetails();
    }
  }, [id]); // loadDisposalRequestDetails will be stable after first render

  const loadDisposalRequestDetails = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getDisposalRequestById(Number(id)) as any;
      setDisposalRequest(response.disposalRequest);
      setDisposalItems(response.disposalItems || []);
    } catch (error: any) {
      let errorMessage = 'Lỗi khi tải chi tiết yêu cầu thanh lý';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colorMap: { [key: string]: string } = {
      'pending': 'orange',
      'approved': 'green',
      'rejected': 'red',
    };
    return colorMap[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const textMap: { [key: string]: string } = {
      'pending': 'Chờ duyệt',
      'approved': 'Đã duyệt (Hoàn thành)',
      'rejected': 'Từ chối',
    };
    return textMap[status] || status;
  };

  const handleApprove = async (values: { approval_document_code: string }) => {
    if (!disposalRequest) return;

    try {
      await apiClient.approveDisposalRequest(disposalRequest.id, {
        approval_document_code: values.approval_document_code
      });
      message.success('Duyệt yêu cầu thanh lý thành công');
      setApproveModalVisible(false);
      approveForm.resetFields();
      loadDisposalRequestDetails(); // Reload để cập nhật trạng thái
    } catch (error: any) {
      let errorMessage = 'Lỗi khi duyệt yêu cầu thanh lý';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      const formattedMessage = errorMessage.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      message.error({
        content: <div dangerouslySetInnerHTML={{ __html: formattedMessage }} />,
        duration: 5
      });
    }
  };

  const showApproveModal = () => {
    setApproveModalVisible(true);
  };

  const disposalItemColumns = [
    {
      title: 'Mã tài sản',
      dataIndex: 'asset_code',
      key: 'asset_code',
      render: (code: string) => (
        <span style={{ fontWeight: 'bold', color: '#1890ff' }}>{code}</span>
      ),
    },
    {
      title: 'Tên tài sản',
      dataIndex: 'asset_name',
      key: 'asset_name',
    },
    ...(user?.role !== 'super_admin' ? [{
      title: 'Công ty',
      dataIndex: 'company_name',
      key: 'company_name',
    }] : []),
    {
      title: 'Nhân viên sử dụng',
      dataIndex: 'employee_name',
      key: 'employee_name',
    },
    {
      title: 'Vị trí',
      dataIndex: 'location_name',
      key: 'location_name',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'asset_status',
      key: 'asset_status',
      render: (status: string) => (
        <Tag color={status === 'disposed' ? 'red' : 'blue'}>
          {status === 'disposed' ? 'Đã thanh lý' : 'Đang sử dụng'}
        </Tag>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!disposalRequest) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Title level={3}>Không tìm thấy yêu cầu thanh lý</Title>
        <Button type="primary" onClick={() => navigate('/disposal')}>
          Quay lại danh sách
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/disposal')}
          style={{ padding: 4, height: 'auto' }}
        />
        <Title level={2} style={{ margin: 0 }}>
          Chi tiết Yêu cầu Thanh lý: {disposalRequest.request_code}
        </Title>
      </div>

      <Card>
        <Descriptions title="Thông tin Yêu cầu Thanh lý" bordered column={2}>
          <Descriptions.Item label="Mã yêu cầu">
            <Text strong style={{ color: '#1890ff' }}>
              {disposalRequest.request_code}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Trạng thái">
            <Tag color={getStatusColor(disposalRequest.status)}>
              {getStatusText(disposalRequest.status)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Tiêu đề">
            {disposalRequest.title}
          </Descriptions.Item>
          <Descriptions.Item label="Ngày tạo">
            {dayjs(disposalRequest.created_at).format('DD/MM/YYYY')}
          </Descriptions.Item>
          <Descriptions.Item label="Người tạo">
            {disposalRequest.requested_by_name || 'N/A'}
          </Descriptions.Item>
          {disposalRequest.company_name && user?.role !== 'super_admin' && (
            <Descriptions.Item label="Công ty">
              {disposalRequest.company_name}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Lý do thanh lý" span={2}>
            {disposalRequest.reason}
          </Descriptions.Item>
          {disposalRequest.status === 'approved' && (
            <>
              <Descriptions.Item label="Người duyệt">
                {disposalRequest.approved_by_name || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Ngày duyệt">
                {disposalRequest.approval_date ? dayjs(disposalRequest.approval_date).format('DD/MM/YYYY') : 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Mã hồ sơ duyệt" span={2}>
                {disposalRequest.approval_document_code || 'Không có'}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>
      </Card>

      <Divider />

      <Card title={`Danh sách Tài sản (${disposalItems.length} tài sản)`}>
        <Table
          columns={disposalItemColumns}
          dataSource={disposalItems}
          rowKey="id"
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {disposalRequest.status === 'pending' && (
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            size="large"
            onClick={showApproveModal}
          >
            Duyệt Yêu cầu
          </Button>
        </div>
      )}

      {/* Modal Duyệt Yêu cầu Thanh lý */}
      <Modal
        title="Duyệt Yêu cầu Thanh lý"
        open={approveModalVisible}
        onCancel={() => {
          setApproveModalVisible(false);
          approveForm.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={approveForm}
          layout="vertical"
          onFinish={handleApprove}
        >
          <Form.Item
            label="Mã hồ sơ duyệt"
            name="approval_document_code"
            rules={[
              { required: true, message: 'Vui lòng nhập mã hồ sơ duyệt' }
            ]}
          >
            <Input placeholder="Nhập mã hồ sơ duyệt" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setApproveModalVisible(false);
                approveForm.resetFields();
              }}>
                Hủy
              </Button>
              <Button type="primary" htmlType="submit">
                Duyệt
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DisposalDetailPage; 